import { NM, PRE_MONTHS, TOTAL_COST_MONTHS, toArrayIdx } from './constants';
import { fu, fp, fpd } from './format';

const STEAM_THRESHOLD = 10_000_000;
const STEAM_RATE_LOW  = 0.30;
const STEAM_RATE_HIGH = 0.25;

/**
 * Calculate blended Steam fee for a month.
 * cumBefore = cumulative gross BEFORE this month.
 * gross = this month's gross revenue.
 */
function steamFee(cumBefore, gross) {
  if (gross <= 0) return { fee: 0, feePct: 0 };
  const cumAfter = cumBefore + gross;

  if (cumBefore >= STEAM_THRESHOLD) {
    // Already over threshold — full low rate
    return { fee: gross * STEAM_RATE_HIGH, feePct: STEAM_RATE_HIGH };
  }
  if (cumAfter <= STEAM_THRESHOLD) {
    // Still under threshold — full high rate
    return { fee: gross * STEAM_RATE_LOW, feePct: STEAM_RATE_LOW };
  }
  // Crossover month — blend
  const belowPart = STEAM_THRESHOLD - cumBefore;
  const abovePart = cumAfter - STEAM_THRESHOLD;
  const fee = (belowPart * STEAM_RATE_LOW) + (abovePart * STEAM_RATE_HIGH);
  return { fee, feePct: fee / gross };
}

/**
 * Calculate revenue for a single platform config.
 */
export function calcPlatform(platKey, st, cumulativeGross = 0) {
  // Other platform: net revenue = platformDeal + customRow1 + customRow2
  if (platKey === 'other') {
    const gross = [], feePct = [], net = [], grossRaw = [], netRaw = [], rrpDisplay = [];
    for (let i = 0; i < NM; i++) {
      const pd = parseFloat(st.platformDeal?.[i]) || 0;
      const rs = parseFloat(st.resellers?.[i]) || 0;
      const ph = parseFloat(st.physical?.[i]) || 0;
      const od = parseFloat(st.otherDeal?.[i]) || 0;
      // Also include legacy customRow1/2 if present
      const c1 = parseFloat(st.customRow1?.[i]) || 0;
      const c2 = parseFloat(st.customRow2?.[i]) || 0;
      const total = pd + rs + ph + od + c1 + c2;
      if (total === 0) {
        gross.push('—'); feePct.push('—'); net.push('—');
        grossRaw.push(null); netRaw.push(null); rrpDisplay.push('—');
      } else {
        gross.push(total); feePct.push('—'); net.push(total);
        grossRaw.push(total); netRaw.push(total); rrpDisplay.push('—');
      }
    }
    return { gross, feePct, net, grossRaw, netRaw, rrpDisplay };
  }

  const ea = st.launchEA !== '' ? parseInt(st.launchEA) : null;
  const fl = st.launchFL !== '' ? parseInt(st.launchFL) : null;

  const gross = [], feePct = [], net = [], grossRaw = [], netRaw = [], rrpDisplay = [];
  let cum = cumulativeGross;

  for (let i = 0; i < NM; i++) {
    const u = parseFloat(st.units[i]) || 0;
    const r = (parseFloat(st.regionalPct[i]) || 0) / 100;
    const d = (parseFloat(st.discountPct[i]) || 0) / 100;

    const aea = ea !== null && i >= ea;
    const afl = fl !== null && i >= fl;

    if (!aea && !afl) {
      gross.push('—'); feePct.push('—'); net.push('—');
      grossRaw.push(null); netRaw.push(null); rrpDisplay.push('—');
      continue;
    }

    let rrp = 0;
    if (afl && st.rrpFL !== '') rrp = parseFloat(st.rrpFL) || 0;
    else if (aea && st.rrpEA !== '') rrp = parseFloat(st.rrpEA) || 0;
    rrpDisplay.push(rrp ? '$' + rrp.toFixed(2) : '—');

    // Days-on-discount formula: units × RRP × (1-regional%) × ((1-discount%) × (daysDiscount/daysInMonth) + (daysFullPrice/daysInMonth))
    // Simplified: units × RRP × (1-regional%) × (1 - discount% × daysDiscount/daysInMonth)
    const daysDiscount = parseFloat(st.daysDiscount?.[i]) || 0;
    const daysInMonth = 30; // standard month approximation
    const effectiveDiscount = daysInMonth > 0 ? d * (daysDiscount / daysInMonth) : d;
    const g = u * rrp * (1 - r) * (1 - effectiveDiscount);

    let fee, feeRate;
    if (platKey === 'steam') {
      const result = steamFee(cum, g);
      fee     = result.fee;
      feeRate = result.feePct;
    } else if (platKey === 'ios' || platKey === 'android') {
      feeRate = 0.30;
      fee     = g * feeRate;
    } else {
      feeRate = 0.30;
      fee     = g * feeRate;
    }

    const n = g - fee;
    cum += g;

    gross.push(fu(g));
    feePct.push(fpd(feeRate * 100));
    net.push(fu(n));
    grossRaw.push(g);
    netRaw.push(n);
  }

  return { gross, feePct, net, grossRaw, netRaw, rrpDisplay };
}

/**
 * Sum net revenues across all enabled platforms for a game.
 */
export function sumNetRevenue(PS, gameName, PLATS) {
  const totals = Array(NM).fill(0);
  PLATS.forEach(p => {
    const st = PS[gameName]?.[p.key];
    if (!st?.enabled) return;
    calcPlatform(p.key, st).netRaw.forEach((v, i) => {
      if (v !== null) totals[i] += v;
    });
  });
  return totals;
}

/**
 * Calculate royalties payable.
 */
export function calcRoyalties(netByMonth, recoupCostsByMonth, dealTerms) {
  const pR = (parseFloat(dealTerms.royaltyPreRecoup) || 0) / 100;
  const qR = (parseFloat(dealTerms.royaltyPostRecoup) || 0) / 100;

  const nA = [], cA = [], rA = [], yA = [], yARaw = [];
  let cum = 0;

  for (let i = 0; i < NM; i++) {
    const n = netByMonth[i] || 0;
    const r = recoupCostsByMonth[i] || 0;

    if (n === 0 && r === 0) {
      nA.push('—'); cA.push(fu(cum)); rA.push('—'); yA.push('—'); yARaw.push(0);
      continue;
    }
    if (n === 0 && r > 0) {
      cum -= r;
      nA.push(fu(-r)); cA.push(fu(cum)); rA.push('—'); yA.push('—'); yARaw.push(0);
      continue;
    }

    const nlr = n - r;
    const cb  = cum;
    cum += nlr;

    let roy = 0, rl = '';
    if (pR === 0 && qR === 0) {
      rl = '—';
    } else if (cb <= 0) {
      rl  = fpd(pR * 100) + ' pre';
      roy = pR * n;
    } else {
      rl  = fpd(qR * 100) + ' post';
      roy = qR * nlr;
    }

    if (cb <= 0 && cum > 0 && nlr > 0) {
      const rf = Math.min(1, Math.max(0, -cb / nlr));
      roy = (pR * n * rf) + (qR * nlr * (1 - rf));
      rl  = 'Split';
    }

    nA.push(fu(nlr));
    cA.push(fu(cum));
    rA.push(rl);
    yA.push(fu(roy));
    yARaw.push(roy);
  }

  return {
    netLessRecoup:    nA,
    cumulative:       cA,
    rateApplied:      rA,
    royaltyPayable:   yA,
    royaltyPayableRaw: yARaw,
  };
}

/**
 * Determine game state based on launch date and recoup position.
 * currentMonthIdx: 0-based index of the current month-end (from dropdown).
 */
export function getGameState(PS, CS, DT, gameName, PLATS, currentMonthIdx) {
  // Use NM-1 (last month) as fallback if no current month set
  const monthIdx = (currentMonthIdx !== null && currentMonthIdx !== undefined)
    ? currentMonthIdx
    : NM - 1;

  // Find earliest launch date (EA takes precedence)
  let launchIdx = null;
  PLATS.forEach(p => {
    const st = PS[gameName]?.[p.key];
    if (!st?.enabled) return;
    const ea = st.launchEA !== '' ? parseInt(st.launchEA) : null;
    const fl = st.launchFL !== '' ? parseInt(st.launchFL) : null;
    const earliest = ea !== null ? ea : fl;
    if (earliest !== null && (launchIdx === null || earliest < launchIdx)) {
      launchIdx = earliest;
    }
  });

  // No platforms enabled — unknown
  const anyEnabled = PLATS.some(p => PS[gameName]?.[p.key]?.enabled);
  if (!anyEnabled) return 'Unknown';

  // Pre-Launch: no launch date set or launch is after current month
  if (launchIdx === null || launchIdx > monthIdx) return 'Pre-Launch';

  // Calculate cumulative net revenue and recoupable costs up to current month
  let cumNet = 0, cumCosts = 0;
  PLATS.forEach(p => {
    const st = PS[gameName]?.[p.key];
    if (!st?.enabled) return;
    calcPlatform(p.key, st).netRaw.forEach((v, i) => {
      if (i <= monthIdx && v !== null) cumNet += v;
    });
  });

  const costs = CS[gameName]?.['__game__'];
  if (costs) {
    ['devAdvances','loc','qa','otherPub','marketing'].forEach(f => {
      (costs.recoup[f] || []).forEach((v, arrayIdx) => {
        // Convert array index to month index
        const mi = arrayIdx - PRE_MONTHS;
        if (mi <= monthIdx) cumCosts += parseFloat(v) || 0;
      });
    });
    const dt = DT[gameName];
    if (dt?.devAdvanceAmt) cumCosts += parseFloat(dt.devAdvanceAmt) || 0;
  }

  if (cumNet >= cumCosts) return 'Recouped';
  return 'Pre-Recoup';
}

/**
 * Calculate IRR using Newton-Raphson method.
 * cashflows: array of { period: 0-based, value: number }
 * Returns IRR as decimal (e.g. 0.15 = 15%) or null if no solution.
 */
export function calcIRR(cashflows) {
  if (!cashflows || cashflows.length < 2) return null;
  
  // Check there's at least one negative and one positive cashflow
  const hasNeg = cashflows.some(c => c < 0);
  const hasPos = cashflows.some(c => c > 0);
  if (!hasNeg || !hasPos) return null;

  let r = 0.1; // initial guess 10%
  
  for (let iter = 0; iter < 100; iter++) {
    let npv = 0, dnpv = 0;
    for (let i = 0; i < cashflows.length; i++) {
      const cf = cashflows[i];
      const disc = Math.pow(1 + r, i);
      npv  += cf / disc;
      dnpv -= i * cf / (disc * (1 + r));
    }
    
    if (Math.abs(dnpv) < 1e-10) break;
    const rNew = r - npv / dnpv;
    if (Math.abs(rNew - r) < 1e-8) { r = rNew; break; }
    r = rNew;
    if (r <= -1) return null; // diverged
  }
  
  return Math.abs(r) > 10 ? null : r; // sanity check
}

/**
 * Build monthly cashflows for a game (net revenue less all costs).
 * Returns array of 36 monthly net cashflows.
 */
export function getGameCashflows(PS, CS, DT, gameName, PLATS) {
  // Extended cashflow: PRE_MONTHS before forecast + NM forecast months
  const totalLen = PRE_MONTHS + NM;
  const netRev   = Array(totalLen).fill(0);
  const allCosts = Array(totalLen).fill(0);

  // Revenue only in forecast period (array indices PRE_MONTHS to PRE_MONTHS+NM-1)
  PLATS.forEach(p => {
    const st = PS[gameName]?.[p.key];
    if (!st?.enabled) return;
    calcPlatform(p.key, st).netRaw.forEach((v, i) => {
      if (v !== null) netRev[PRE_MONTHS + i] += v;
    });
  });

  // Costs span the full array (pre-forecast + forecast)
  const costs = CS[gameName]?.['__game__'];
  if (costs) {
    ['recoup', 'nonRecoup'].forEach(sec => {
      ['devAdvances','loc','qa','otherPub','marketing'].forEach(f => {
        (costs[sec][f] || []).forEach((v, arrayIdx) => {
          allCosts[arrayIdx] += parseFloat(v) || 0;
        });
      });
    });
  }

  return Array.from({ length: totalLen }, (_, i) => netRev[i] - allCosts[i]);
}
